import { DesignTokens, Checkbox, Spin, Title } from "melco-ui";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash-es";

import { DisplaySummitDetailsMachineStatus } from "./DisplaySummitDetailsMachineStatus";
import { CenteredMessage, StepLabel, ShowAllMachinesRow } from "./summitStyled";

const { spacings, colors } = DesignTokens;

type MachinesPanelProps = {
  selectedUser: any | null;
  selectedAddOn: string;
  /** Only sub-users have a parent account whose machines could be pulled
   * in, so the "show all machines" toggle only makes sense for them. */
  isSubUser: boolean;
  showAllMachines: boolean;
  onShowAllMachinesChange: (checked: boolean) => void;
  machineFilterText: string;
  onMachineFilterTextChange: (value: string) => void;
  machinesLoading: boolean;
  visibleMachines: any[];
  selectedMachine: any;
  onMachineSelect: (machine: any, deviceId: string) => void;
};

// WHAT: the middle "Step 2 — select machine" column.
// WHY a dedicated component: this used to be ~90 lines inlined in
// SummitWorkspace's JSX, mixed in with the other two panels' markup.
// Pulling it out gives it a single job (render machine-selection UI for
// whatever data it's handed) and makes it possible to unit-test the
// panel's four states (no user, no add-on, loading, empty, populated)
// without also standing up the machines/logs data-fetching that lives
// in the parent.
export const MachinesPanel: React.FC<MachinesPanelProps> = ({
  selectedUser,
  selectedAddOn,
  isSubUser,
  showAllMachines,
  onShowAllMachinesChange,
  machineFilterText,
  onMachineFilterTextChange,
  machinesLoading,
  visibleMachines,
  selectedMachine,
  onMachineSelect,
}) => {
  const { t } = useTranslation();

  if (!selectedUser) {
    return (
      <>
        <PanelLabel />
        <CenteredMessage>
          {t("summit.select_user_prompt", {
            defaultValue: "Select a user to see their machines",
          })}
        </CenteredMessage>
      </>
    );
  }

  if (isEmpty(selectedAddOn)) {
    // Validation: a user is selected but no add-on has been chosen yet,
    // so there's nothing to fetch machines for. Kept as its own message
    // (rather than folded into the "select a user" prompt above) so the
    // rep knows exactly what's missing instead of wondering why nothing
    // loaded after they already picked a user. The same guidance is now
    // also shown right under the add-on field itself (SelectSummitAddOn)
    // so it's visible at the point of the decision, not just here.
    return (
      <>
        <PanelLabel />
        <CenteredMessage>
          {t("summit.select_addon_prompt", {
            defaultValue: "Select an add-on above to load this user's machines.",
          })}
        </CenteredMessage>
      </>
    );
  }

  return (
    <>
      <PanelLabel />
      <div style={{ padding: `0px ${spacings.middle}px` }}>
        <Title level={5}>{t("summit.machines")}</Title>
      </div>

      {!isSubUser && (
        <ShowAllMachinesRow>
          <Checkbox
            checked={showAllMachines}
            onChange={(e: any) => onShowAllMachinesChange(!!e?.target?.checked)}
          >
            {t("summit.show_all_machines", { defaultValue: "Show all machines" })}
          </Checkbox>
        </ShowAllMachinesRow>
      )}

      <div
        style={{
          padding: `${spacings.small}px ${spacings.middle}px`,
          borderBottom: `1px solid ${colors.gray.light}`,
        }}
      >
        <input
          type="text"
          placeholder={t("summit.search_machines", { defaultValue: "Filter by name or device ID…" })}
          value={machineFilterText}
          onChange={(e) => onMachineFilterTextChange(e.target.value)}
          style={{
            width: "100%",
            padding: "6px 9px",
            fontSize: 13,
            border: `1px solid ${colors.gray.light}`,
            borderRadius: 4,
          }}
        />
        <div style={{ fontSize: 11, color: colors.gray.middle, textAlign: "right", marginTop: 4 }}>
          {visibleMachines.length} {t("summit.machines")}
        </div>
      </div>

      {machinesLoading ? (
        <CenteredMessage>
          <Spin tip={t("summit.loading_machines")} />
        </CenteredMessage>
      ) : isEmpty(visibleMachines) ? (
        <CenteredMessage>{t("summit.no_machines_found")}</CenteredMessage>
      ) : (
        <div style={{ flex: 1, overflowY: "auto" }}>
          <DisplaySummitDetailsMachineStatus
            filteredMachines={visibleMachines}
            onClick={onMachineSelect}
            selectedMachine={selectedMachine}
          />
        </div>
      )}
    </>
  );
};

// Small helper so the "Step 2 — Select Machine" eyebrow label doesn't
// need its padding/wrapper repeated at all four return points above.
//
// FIXME: the translation key below is `step_one_label` but the English
// fallback text says "Step 2" — this mismatch predates this refactor
// (same key was already used for the Step 2 label before). Left as-is
// rather than silently changed, since I don't know whether translation
// files elsewhere already key off `step_one_label` for this string.
// Worth confirming with whoever owns the translation files.
const PanelLabel: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div style={{ padding: `${spacings.middle}px ${spacings.middle}px 0px ${spacings.middle}px` }}>
      <StepLabel>
        {t("summit.form.panel.step_one_label", { defaultValue: "Step 2 — Select Machine" })}
      </StepLabel>
    </div>
  );
};
